import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/core';
import { showToast } from 'components/common/CustomToast';
import { useAttachmentStore } from 'stores/attachmentStore';
import { CREATE_WAITING_LIST_ENTRY } from 'graphql/mutations/createWaitingListEntry';
import { GET_UNIFIED_PERMITS } from 'graphql/queries/getUnifiedPermits';
import {
  CountryCode,
  Mutation,
  MutationCreateWaitingListEntryArgs,
} from 'types/generatedSchemaTypes';
import { LongTermNavProps } from 'types/navigation';

interface JoinWaitingListParams {
  pmcId: string;
  countryCode: CountryCode;
  customerEmail: string;
  billingInterval: string;
  includeAttachments?: boolean;
}

export const useJoinWaitingList = () => {
  const { navigate } = useNavigation<LongTermNavProps>();

  const allAttachments = useAttachmentStore((state) => state.attachments);
  const ctxId = useAttachmentStore((state) => state.currentAttachmentContext);

  const [createWaitingListEntry, { loading }] = useMutation<
    Mutation,
    MutationCreateWaitingListEntryArgs
  >(CREATE_WAITING_LIST_ENTRY, {
    refetchQueries: [GET_UNIFIED_PERMITS],
    awaitRefetchQueries: true,
    onError: (error) => {
      if (
        error.graphQLErrors.some(
          (e) => e.extensions?.code === 'WAITINGLIST_ENTRY_ALREADY_EXISTS'
        )
      ) {
        showToast('error.waitingListEntryAlreadyExists', 'error');
      } else {
        showToast('error.genericError', 'error');
      }
    },
  });

  const joinWaitingList = ({
    pmcId,
    countryCode,
    customerEmail,
    billingInterval,
    includeAttachments = true,
  }: JoinWaitingListParams) => {
    const ctx = ctxId ?? pmcId;

    const uploadedForCtx = includeAttachments
      ? allAttachments
          .filter((x) => x.contextId === ctx && !!x.uploadUid)
          .map((x) => ({
            uid: x.uploadUid!,
            fileName: x.uploadFileName ?? x.name,
            mimeType: x.uploadMimeType ?? x.type ?? null,
            sizeBytes: x.uploadSizeBytes ?? x.size ?? null,
          }))
      : [];

    createWaitingListEntry({
      variables: {
        input: {
          pmcId,
          countryCode,
          customerEmail,
          orderDetails: {
            billingInterval,
            ...(uploadedForCtx.length ? { attachments: uploadedForCtx } : {}),
          },
        },
      },
      onCompleted: () => navigate('waitingListCompleted', { pmcId }),
    });
  };

  return { joinWaitingList, loading };
};
